import type { Backend } from '@freelancer/datastore/core';
import { OrderByDirection } from '@freelancer/datastore/core';
import { isDefined } from '@freelancer/utils';
import { EnterpriseSourceApi } from 'api-typings/common/common';
import type {
  MarketingMobileNumberRequestApi,
  UserOperatingAreaUpdateRequestApi,
} from 'api-typings/users/users';
import type { UserAccountDirectoryAndFollowPrefsPayload } from './users-self.backend-model';
import type {
  EnterpriseUpdatePayload,
  EquipmentGroupItemsAddPayload,
  ProfileUpdatePayload,
  UserAccountRoleUpdatePayload,
} from './users-self.model';
import type { UsersSelfCollection } from './users-self.types';
import { Enterprise } from '../enterprise/enterprise.model';

export function usersSelfBackend(): Backend<UsersSelfCollection> {
  return {
    defaultOrder: {
      field: 'id',
      direction: OrderByDirection.DESC,
    },
    // FIXME: T50039 This endpoint doesn't appear to be ordered in the backend
    fetch: (authUid, ids, query, order) => ({
      endpoint: 'users/0.1/self',
      params: {
        status: 'true',
        marketing_mobile_number: true,
        limited_account: 'true',
        secure_phone: true,
        directory_follow_preferences: true,
        nominated_payment_control_status: true,
        operating_areas: true,
        equipment_group_details: true,
      },
    }),
    push: undefined,
    set: undefined,
    update: (authUid, delta, original) => {
      let payload:
        | EnterpriseUpdatePayload
        | ProfileUpdatePayload
        | MarketingMobileNumberRequestApi
        | UserAccountRoleUpdatePayload
        | UserAccountDirectoryAndFollowPrefsPayload
        | UserOperatingAreaUpdateRequestApi
        | EquipmentGroupItemsAddPayload
        | undefined;
      let endpoint = 'users/0.1/self/profile';

      if (isDefined(delta.chosenRole)) {
        payload = { chosen_role: delta.chosenRole };
        endpoint = 'users/0.1/self/account';
      }

      if (isDefined(delta.directoryAndFollowPreferences)) {
        if (payload) {
          throw new Error('Cannot update two or more fields at once.');
        }

        const { directoryAndFollowPreferences } = delta;

        payload = {
          directory_follow_preferences: {
            allow_listing: directoryAndFollowPreferences.allowListing,
            allow_watch: directoryAndFollowPreferences.allowWatch,
            allow_seal_price_on_review:
              directoryAndFollowPreferences.allowSealPriceOnReview,
          },
        };

        endpoint = 'users/0.1/self/account';
      }

      if (delta.hasLinkedEscrowComAccount === true) {
        if (payload) {
          throw new Error('Cannot update two or more fields at once.');
        }
        payload = {
          action: 'escrowcom_link_consent',
        };
        endpoint = 'users/0.1/self';
      }

      if (delta.primaryLanguage) {
        if (payload) {
          throw new Error('Cannot update two or more fields at once.');
        }
        payload = {
          primary_language: delta.primaryLanguage,
        };
      }

      if (delta.marketingMobileNumber) {
        if (payload) {
          throw new Error('Cannot update two or more fields at once.');
        }

        const { marketingMobileNumber } = delta;
        if (
          !marketingMobileNumber.phoneNumber ||
          !marketingMobileNumber.countryCode
        ) {
          throw new Error('Invalid marketing mobile number.');
        }
        return {
          endpoint: `users/0.1/self/marketing_mobile_number`,
          method: 'POST',
          payload: {
            phone_number: marketingMobileNumber.phoneNumber,
            country_code: marketingMobileNumber.countryCode,
          },
        };
      }

      if (
        delta.enterpriseIds?.includes(Enterprise.FACEBOOK) &&
        (original.enterpriseIds
          ? !original.enterpriseIds.includes(Enterprise.FACEBOOK)
          : true)
      ) {
        if (payload) {
          throw new Error('Cannot update two or more fields at once.');
        }

        const enterprise = Enterprise.FACEBOOK;
        const source = EnterpriseSourceApi.FACEBOOK;

        return {
          endpoint: 'users/0.1/self/',
          method: 'PUT',
          payload: {
            action: 'add_enterprise',
            enterprise,
            source,
          },
        };
      }

      if (isDefined(delta.primaryCurrency)) {
        if (payload) {
          throw new Error('Cannot update two or more fields at once.');
        }

        const { id: currencyId } = delta.primaryCurrency;

        if (!isDefined(currencyId)) {
          throw new Error('Currency ID is required.');
        }

        return {
          endpoint: 'users/0.1/self/primary_currency',
          method: 'PUT',
          payload: { currency_id: currencyId },
        };
      }

      if (isDefined(delta.operatingAreas)) {
        if (payload) {
          throw new Error('Cannot update two or more fields at once.');
        }

        const { operatingAreas } = delta;

        if (!isDefined(operatingAreas)) {
          throw new Error('Operating area ids are required.');
        }

        return {
          endpoint: 'users/0.1/self/operating_areas',
          method: 'PUT',
          payload: {
            user_id: original.id,
            operating_area_ids: operatingAreas
              .map(area => area?.id)
              .filter(isDefined),
          },
        };
      }

      if (isDefined(delta.equipmentGroups)) {
        if (payload) {
          throw new Error('Cannot update two or more fields at once.');
        }

        const { equipmentGroups } = delta;

        if (!isDefined(equipmentGroups)) {
          throw new Error('Equipment items are required.');
        }

        const equipmentGroupItems = equipmentGroups
          .filter(isDefined)
          .map(group => {
            if (!isDefined(group.id)) {
              throw new Error('Equipment group ids are required.');
            }

            const items = group.items ? group.items : [];
            const filteredItems = items.filter(isDefined).map(equipment => {
              if (!isDefined(equipment.id)) {
                throw new Error('Equipment ids are required.');
              }
              return {
                id: equipment.id,
                name: equipment.name,
              };
            });

            return {
              id: group.id,
              items: filteredItems,
            };
          });
        return {
          endpoint: 'users/0.1/self/equipment_items',
          method: 'POST',
          payload: {
            items: equipmentGroupItems,
          },
        };
      }

      if (!payload) {
        throw new Error('Nothing to be updated.');
      }

      return { endpoint, method: 'PUT', payload };
    },
    remove: undefined,
  };
}
